/**
## Content Overflow Tooltip Manager

`sq-content-overflow-tooltip-manager` is meant to be a fairly universal wrapper component
that has no definition on its own. Instead, based on criteria provided, it dynamically
searches for content inside that is truncated, and adds CSS-based tooltips to show the
full content on hover. An alternative standard for touch-based clients will be added later,
likely to be a two or three finger tap.

The most common usage case are with tables that have important content that tends to run
extremely long or where column width trends narrow. However, this can also be used on forms or
other types of content.

Important note: ensure that your truncation method for the content you are checking relies on
standard text ellipsis rules (`overflow: hidden`, `white-space: no-wrap`). This component uses
a simple switch to `white-space: normal` for comparison.


```example
@title Basic Example

@controller-properties
{
}

@hbs
{{#sq-content-overflow-tooltip-manager criteria=".name"}}
  <table style="max-width: 500px;">
    <thead>
      <tr>
        <th>Name</th>
        <th>Alpha</th>
        <th>Beta</th>
        <th>Delta</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td class="name">Short</td>
        <td>1</td>
        <td>2</td>
        <td>3</td>
      </tr>
      <tr>
        <td class="name">Medium</td>
        <td>1</td>
        <td>2</td>
        <td>3</td>
      </tr>
      <tr>
        <td class="name">Long Lorem ipsum dolor sit amet, consectetur adipisicing elit. Necessitatibus magnam ipsum repellat aspernatur nisi velit quod rem dignissimos eum.</td>
        <td>1</td>
        <td>2</td>
        <td>3</td>
      </tr>
      <tr>
        <td class="name">Long Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ex assumenda quasi tenetur id, quo ipsum soluta nobis libero culpa, dolor nihil.</td>
        <td>1</td>
        <td>2</td>
        <td>3</td>
      </tr>
      <tr>
        <td class="name">Long Lorem ipsum dolor sit amet, consectetur adipisicing elit. Magni, libero cum dolor. Ex veniam tenetur recusandae minus assumenda, quo dolores saepe, error impedit sed dolore vitae, quia expedita atque itaque!</td>
        <td>1</td>
        <td>2</td>
        <td>3</td>
      </tr>
    </tbody>
  </table>
{{/sq-content-overflow-tooltip-manager}}
```

```example
@title Example with using tagName and criteria to creatively to avoid adding more elements

@controller-properties
{
}

@hbs
{{#sq-content-overflow-tooltip-manager criteria=".name"
                                       tagName='table'}}
  <thead>
    <tr>
      <th>Name</th>
      <th>Alpha</th>
      <th>Beta</th>
      <th>Delta</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td class="name">Short</td>
      <td>1</td>
      <td>2</td>
      <td>3</td>
    </tr>
    <tr>
      <td class="name">Medium</td>
      <td>1</td>
      <td>2</td>
      <td>3</td>
    </tr>
    <tr>
      <td class="name">Long Lorem ipsum dolor sit amet, consectetur adipisicing elit. Necessitatibus magnam ipsum repellat aspernatur nisi velit quod rem dignissimos eum.</td>
      <td>1</td>
      <td>2</td>
      <td>3</td>
    </tr>
    <tr>
      <td class="name">Long Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ex assumenda quasi tenetur id, quo ipsum soluta nobis libero culpa, dolor nihil.</td>
      <td>1</td>
      <td>2</td>
      <td>3</td>
    </tr>
    <tr>
      <td class="name">Long Lorem ipsum dolor sit amet, consectetur adipisicing elit. Magni, libero cum dolor. Ex veniam tenetur recusandae minus assumenda, quo dolores saepe, error impedit sed dolore vitae, quia expedita atque itaque!</td>
      <td>1</td>
      <td>2</td>
      <td>3</td>
    </tr>
  </tbody>
{{/sq-content-overflow-tooltip-manager}}
```


### Parameters
@param {String} criteria - Required, a selection string to target elements within the content of this wrapper component. Use any Sizzle friendly CSS selector syntax. As always, targetting classes is more efficient than elements.
@param {Boolean} useTruncationCache - Controls whether we should recalculate if we need to show the tooltip everytime the tracked element is hovered over. Defaults to true

Sass dependencies at:
`app/assets/stylesheets/dashboard/components/_sq-content-overflow-tooltip-mananger.sass`
*/

import { throttle, scheduleOnce } from '@ember/runloop';
import { assert } from '@ember/debug';
import Component from '@ember/component';
import Ember from 'ember';
import $ from 'jquery';
import { measureTextWidth } from '../utils/input';
const THROTTLE_PERIOD = 50;
const THROTTLE_PERIOD_RESIZE = 1000;
// because we're referencing pseudo elements, we can't grab this dynamically, it's part of the DOM.
// also a performance boost when we don't make the extra reference. But we have to update this accordingly
// when the tooltip styling is updated.

// horizontal offset = tooltip side padding + borders - left offset
const TOOLTIP_HORIZONTAL_OFFSET = 34;
// vertical offset = tooltip top/bottom padding + (font size * line height) + borders - top offset
const TOOLTIP_VERTICAL_OFFSET = 31;

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['content-overflow-tooltip-manager'],
  // passed in
  criteria: null,
  useTruncationCache: true,
  // lifecycle
  didInsertElement(...args) {
    this._super(...args);
    const criteria = this.get('criteria');
    assert('criteria required', criteria);
    if (criteria) {
      const self = this; // eslint-disable-line unicorn/no-this-assignment
      // eslint-disable-next-line ember/no-jquery
      $(this.element).on(`mouseenter.contentOverflowTooltipManager${this.get('elementId')}`, criteria, function () {
        // In tests, we want to test for multiple lines with overflow content.
        // This requires us to always call checkElementForTruncation and ignore throttling
        // since tests execute way faster than the throttling limit we set for real world use cases.
        // If we throttle, after first `mouseenter`, any following event would be ignored.
        if (Ember.testing) {
          // eslint-disable-next-line ember/no-jquery
          self.checkElementForTruncation($(this));
        } else {
          throttle(self, self.checkElementForTruncation, $(this),
          // eslint-disable-line ember/no-jquery
          THROTTLE_PERIOD);
        }
      });
      // eslint-disable-next-line ember/no-jquery
      $(window).on(`resize.contentOverflowTooltipManager${this.get('elementId')}`, this.windowDidResize.bind(this));
    }
  },
  willDestroyElement(...args) {
    this._super(...args);
    if (this.get('criteria')) {
      // eslint-disable-next-line ember/no-jquery
      $(this.element).off(`mouseenter.contentOverflowTooltipManager${this.get('elementId')}`);
      // eslint-disable-next-line ember/no-jquery
      $(window).off(`resize.contentOverflowTooltipManager${this.get('elementId')}`);
    }
  },
  windowDidResize() {
    throttle(this, 'clearTruncationCache', THROTTLE_PERIOD_RESIZE, false);
  },
  clearTruncationCache() {
    // eslint-disable-next-line ember/no-jquery
    $(this.element).find(this.get('criteria')).data('contentOverflowTooltipChecked', false).removeClass('content-overflow-tooltip-manager__truncated content-overflow-tooltip-manager__truncated--position-top-left content-overflow-tooltip-manager__truncated--position-top-right content-overflow-tooltip-manager__truncated--position-bottom-left content-overflow-tooltip-manager__truncated--position-bottom-right');
  },
  // TODO (nschaden): for now criteria passed into this element only works if the criteria targets the *immediate*
  // element wrapping the content we want to truncate. Examples so far are just a type of simple .table-cell, but
  // can this transfer to other more complex objects?
  checkElementForTruncation(element) {
    // drop out if the element has already been checked for truncation, or the element
    // doesn't exactly match our criteria (generally this should only happen after we've added
    // tooltip content and you are getting a mouse move event where the target is the inner wrapper div work)
    if (element.data('contentOverflowTooltipChecked') && this.useTruncationCache) {
      return;
    }
    element.data('contentOverflowTooltipChecked', true);
    const fullContentWidth = this.contentIsTruncated(element);
    if (fullContentWidth) {
      // determine if tooltip is hitting any window boundaries
      let contentOverflowClass = 'content-overflow-tooltip-manager__truncated';
      const $window = $(window); // eslint-disable-line ember/no-jquery
      const $elementOffset = element.offset();
      const crossingRight = $window.width() < $elementOffset.left + fullContentWidth + TOOLTIP_HORIZONTAL_OFFSET;
      const crossingTop = $window.scrollTop() >= $elementOffset.top - TOOLTIP_VERTICAL_OFFSET;
      if (crossingRight && !crossingTop) {
        contentOverflowClass += ` ${contentOverflowClass}--position-top-left`;
      } else if (crossingRight && crossingTop) {
        contentOverflowClass += ` ${contentOverflowClass}--position-bottom-left`;
      } else if (!crossingRight && crossingTop) {
        contentOverflowClass += ` ${contentOverflowClass}--position-bottom-right`;
      }
      // eslint-disable-next-line ember/no-incorrect-calls-with-inline-anonymous-functions
      scheduleOnce('afterRender', () => {
        element.addClass(contentOverflowClass).attr('data-tooltip', element.text());
        if (!element.children().hasClass('content-overflow-tooltip-manager__original-content')) {
          element.wrapInner('<div class="content-overflow-tooltip-manager__original-content"></div>');
        }
      });
    }
  },
  // helper function to check for truncated content, return full width if we should truncate
  contentIsTruncated(domElement) {
    if (!domElement) {
      return false;
    }
    const elementWidth = domElement.width();
    const nonTruncatedWidth = measureTextWidth(domElement.text());
    return nonTruncatedWidth > elementWidth ? nonTruncatedWidth : false;
  }
});